'use client';

import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { FormContext, type FormContextValue, type FormFieldEntry } from '../../lib/form';

export interface SFormValidationError {
  /** 검증에 실패한 필드 name 목록 (DOM 순서) */
  names: string[];
}

/** ref로 노출되는 폼 제어 메서드 (sd-form @Method 대응) */
export interface SFormHandle {
  /** 등록된 모든 필드를 검증하고 통과 여부를 반환 */
  validate: () => boolean;
  /** 모든 필드의 검증 상태만 초기화 (값은 소비자 소유) */
  resetValidation: () => void;
  /** resetValidation 별칭 — sd-form sdReset 대응. 값 리셋은 소비자(value/onChange) 책임 */
  reset: () => void;
  /** 첫 번째 실패 필드로 포커스 이동 */
  focusFirstInvalid: () => void;
}

export interface SFormProps {
  /** form 엘리먼트에 추가할 클래스 (sd-form formClass 대응) */
  formClass?: string;
  /** 모든 필드 검증 통과 후 발생 (sd-form sdSubmit 대응) */
  onSubmit?: () => void;
  /** 검증 실패 시 실패한 필드 name 목록 전달 (sd-form sdValidationError 대응) */
  onValidationError?: (error: SFormValidationError) => void;
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * SForm — sd-form 포팅. 폼 안의 rule-bearing 컨트롤(useFormField로 등록)을 모아
 * submit 시 일괄 검증한다. 하나라도 실패하면 제출을 막고 첫 실패 필드에 포커스한 뒤
 * onValidationError를 발생시킨다. 값은 여전히 소비자 소유(form-agnostic 유지).
 */
export const SForm = forwardRef<SFormHandle, SFormProps>(function SForm(
  { formClass, onSubmit, onValidationError, children, className, style },
  ref,
) {
  const formRef = useRef<HTMLFormElement>(null);
  const fieldsRef = useRef<Set<FormFieldEntry>>(new Set());

  const register = useCallback((entry: FormFieldEntry) => {
    fieldsRef.current.add(entry);
    return () => {
      fieldsRef.current.delete(entry);
    };
  }, []);

  const ctx = useMemo<FormContextValue>(() => ({ register }), [register]);

  /** 실패 필드 name들을 DOM 등장 순서로 정렬 (name / data-name 기준) */
  const orderByDom = useCallback((names: string[]): string[] => {
    const form = formRef.current;
    if (!form || names.length < 2) return names;
    const domOrder = new Map<string, number>();
    form.querySelectorAll<HTMLElement>('[name],[data-name]').forEach((el, i) => {
      const n = el.getAttribute('name') || el.getAttribute('data-name');
      if (n && !domOrder.has(n)) domOrder.set(n, i);
    });
    return [...names].sort((a, b) => (domOrder.get(a) ?? Infinity) - (domOrder.get(b) ?? Infinity));
  }, []);

  const focusByName = useCallback((name: string) => {
    const form = formRef.current;
    if (!form || !name) return;
    const host =
      form.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`) ??
      form.querySelector<HTMLElement>(`[data-name="${CSS.escape(name)}"]`);
    if (!host) return;
    const focusable = host.matches('input,textarea,select,button,[tabindex]')
      ? host
      : host.querySelector<HTMLElement>('input,textarea,select,button,[tabindex]');
    focusable?.focus();
    (focusable ?? host).scrollIntoView({ block: 'nearest' });
  }, []);

  /**
   * 등록된 모든 필드를 검증한다.
   * - valid: 전체 통과 여부 (이름 없는 필드도 실패하면 false → 제출 차단)
   * - names: 실패한 필드 중 name이 있는 것들 (DOM 순서, onValidationError용)
   */
  const runValidation = useCallback((): { valid: boolean; names: string[] } => {
    let valid = true;
    const invalidNames: string[] = [];
    fieldsRef.current.forEach(f => {
      if (!f.validate()) {
        valid = false;
        if (f.name) invalidNames.push(f.name);
      }
    });
    return { valid, names: orderByDom(invalidNames) };
  }, [orderByDom]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => runValidation().valid,
      resetValidation: () => fieldsRef.current.forEach(f => f.resetValidation()),
      reset: () => fieldsRef.current.forEach(f => f.resetValidation()),
      focusFirstInvalid: () => {
        const { names } = runValidation();
        if (names.length > 0) focusByName(names[0]);
      },
    }),
    [runValidation, focusByName],
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { valid, names } = runValidation();
    if (!valid) {
      if (names.length > 0) focusByName(names[0]);
      onValidationError?.({ names });
      return;
    }
    onSubmit?.();
  };

  return (
    <FormContext.Provider value={ctx}>
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className={cn(formClass, className)}
        style={style}
        noValidate
      >
        {children}
      </form>
    </FormContext.Provider>
  );
});
